import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthFromToken } from 'actions/authActions';
import { changeLocale } from 'actions/statusActions';
import api from 'api';
import isMobileAppRuntime, { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { moduleAPI as familyAgeLanguageAPI } from 'mobile-modules/family-age-language';
import {
  consumePendingOtpNotification,
  getMobilePushContext,
  initializeMobilePushBridge,
  MOBILE_OTP_EVENT,
  signInWithAppleNative,
} from 'utils/mobileNativeBridge';
import classes from './MobileWrapperRegistration.module.scss';
import mobileLogo from './Logo-flc.svg';

type Props = { history: any };
type Option = { value: string; label: string };
type Screen = 'splash' | 'auth' | 'verify' | 'basic' | 'learning';
type OtpDeliveryState = {
  active: boolean;
  progress: number;
  message: string;
  detail: string;
};

type LearningValues = {
  name: string;
  country: string;
  ageBand: string;
  instructionLanguage: string;
  accessCode: string;
};

const DEVICE_KEY = 'flcMobileWrapperDeviceId';
const FAMILY_ACCOUNT_TOKEN_KEY = 'flcMobileFamilyAccountToken';
const DEFAULT_COUNTRY = 'United States';
const STRING_NUMBER_BY_COPY_PATH: Record<string, number> = {
  email: 911,
  sendingCode: 3792,
  continueEmail: 3787,
  continueWith: 3790,
  sendingVerification: 3793,
  verifyTitle: 3794,
  verifySubtitle: 3795,
  resend: 3796,
  codeRequested: 3797,
  back: 3798,
  verify: 3799,
  verifyFailed: 3800,
  clear: 3849,
  basicTitle: 3801,
  basicSubtitle: 3802,
  name: 3803,
  namePlaceholder: 3804,
  country: 3805,
  learningTitle: 3806,
  learningSubtitle: 3807,
  selectAge: 3808,
  'ages.age_4_8': 3809,
  'ages.age_9_13': 3810,
  'ages.age_14_18': 3811,
  'ages.adult': 3812,
  instructionLanguage: 3813,
  secondLanguage: 3814,
  none: 3815,
  submit: 3816,
};
const UI_FALLBACK_COPY = {
  en: {
    rotateTitle: 'Please rotate your device',
    rotateText: 'Turn your phone sideways to continue.',
    pageLanguage: 'Page language',
    email: 'Email',
    sendingCode: 'Sending code...',
    continueEmail: 'Continue with Email',
    continueWith: 'or continue with',
    continueApple: 'Continue with Apple ID',
    continueGoogle: 'Continue with Google ID',
    appleSigningIn: 'Signing in with Apple...',
    appleSignInCancelled: 'Sign in with Apple was cancelled.',
    appleSignInFailed: 'Sign in with Apple could not be completed.',
    invalidEmail: 'Enter a valid email address.',
    invalidProviderEmail: (provider: string) => `Enter a valid email address to continue with ${provider}.`,
    providerOtpNotice: (provider: string) => `${provider} email verification started. Check your email for the code.`,
    preparingVerification: 'Preparing verification...',
    checkingNotifications: 'Checking notification access',
    connectingDevice: 'Connecting your device...',
    notificationsAllowed: 'Notifications are allowed on this device',
    emailStillAvailable: 'Email verification remains available',
    sendingVerification: 'Sending verification code...',
    requestingCode: 'Requesting a new 6-digit code',
    unableSend: 'Unable to send verification code.',
    waitingDelivery: 'Waiting for delivery...',
    checkEmail: 'Check your email for the verification code',
    codeRequested: 'Code requested. Check your email.',
    newCodeRequested: 'New code requested. Check your email.',
    verificationRequested: 'Verification code requested',
    pushCodeReceived: 'Verification code received from the app notification.',
    verifyTitle: 'Verify your email',
    verifySubtitle: 'Enter the 6-digit code from your email or app notification',
    digit: 'Digit',
    resendIn: (seconds: number) => `Resend code in ${seconds}s`,
    resend: '↻  Resend code',
    back: 'Back',
    verify: 'Verify',
    enterCode: 'Enter the 6-digit code sent to your email.',
    verifyFailed: 'Email verification could not be completed.',
    clear: 'Clear',
    requestNewCodeIn: (seconds: number) => `You can request a new verification code in ${seconds} seconds.`,
    basicTitle: 'Basic info',
    basicSubtitle: 'Tell us about you',
    name: 'Name',
    namePlaceholder: 'Your name',
    country: 'Country',
    accessCode: 'Access Code (optional)',
    accessCodePlaceholder: 'Enter a reusable or 6-digit code',
    basicRequired: 'Enter your name and select your country.',
    next: 'Next',
    learningTitle: 'Learning setup',
    learningSubtitle: 'Customize your learning experience',
    selectAge: 'Select age group',
    instructionLanguage: 'Instruction language',
    secondLanguage: 'Second instruction language',
    none: 'None',
    loadingLanguages: 'Loading languages…',
    learningRequired: 'Select an age group and an instruction language.',
    noInstructionLanguages: 'No instruction languages are configured for this Mobile App Learning Center.',
    submit: 'Let’s Learn',
    accountFailed: 'Account could not be created.',
    ages: {
      age_4_8: 'Ages 4–8',
      age_9_13: 'Ages 9–13',
      age_14_18: 'Ages 14–18',
      adult: 'Adults',
    },
  },
  es: {
    rotateTitle: 'Gira tu dispositivo',
    rotateText: 'Pon el teléfono en posición horizontal para continuar.',
    pageLanguage: 'Idioma de la página',
    email: 'Correo electrónico',
    sendingCode: 'Enviando código...',
    continueEmail: 'Continuar con correo',
    continueWith: 'o continuar con',
    continueApple: 'Continuar con Apple ID',
    continueGoogle: 'Continuar con Google ID',
    appleSigningIn: 'Iniciando sesión con Apple...',
    appleSignInCancelled: 'Se canceló el inicio de sesión con Apple.',
    appleSignInFailed: 'No se pudo completar el inicio de sesión con Apple.',
    invalidEmail: 'Introduce una dirección de correo válida.',
    invalidProviderEmail: (provider: string) => `Introduce una dirección de correo válida para continuar con ${provider}.`,
    providerOtpNotice: (provider: string) => `Se inició la verificación por correo de ${provider}. Revisa tu correo.`,
    preparingVerification: 'Preparando la verificación...',
    checkingNotifications: 'Comprobando el acceso a notificaciones',
    connectingDevice: 'Conectando tu dispositivo...',
    notificationsAllowed: 'Las notificaciones están permitidas',
    emailStillAvailable: 'La verificación por correo sigue disponible',
    sendingVerification: 'Enviando el código...',
    requestingCode: 'Solicitando un código de 6 dígitos',
    unableSend: 'No se pudo enviar el código de verificación.',
    waitingDelivery: 'Esperando la entrega...',
    checkEmail: 'Revisa tu correo para ver el código',
    codeRequested: 'Código solicitado. Revisa tu correo.',
    newCodeRequested: 'Nuevo código solicitado. Revisa tu correo.',
    verificationRequested: 'Código de verificación solicitado',
    pushCodeReceived: 'Código recibido desde la notificación de la aplicación.',
    verifyTitle: 'Verifica tu correo',
    verifySubtitle: 'Introduce el código de 6 dígitos de tu correo o notificación',
    digit: 'Dígito',
    resendIn: (seconds: number) => `Reenviar código en ${seconds}s`,
    resend: '↻  Reenviar código',
    back: 'Atrás',
    verify: 'Verificar',
    enterCode: 'Introduce el código de 6 dígitos enviado a tu correo.',
    verifyFailed: 'No se pudo completar la verificación del correo.',
    clear: 'Borrar',
    requestNewCodeIn: (seconds: number) => `Puedes solicitar un nuevo código de verificación en ${seconds} segundos.`,
    basicTitle: 'Información básica',
    basicSubtitle: 'Cuéntanos sobre ti',
    name: 'Nombre',
    namePlaceholder: 'Tu nombre',
    country: 'País',
    accessCode: 'Código de acceso (opcional)',
    accessCodePlaceholder: 'Introduce un código reutilizable o de 6 dígitos',
    basicRequired: 'Introduce tu nombre y selecciona tu país.',
    next: 'Siguiente',
    learningTitle: 'Configuración de aprendizaje',
    learningSubtitle: 'Personaliza tu experiencia de aprendizaje',
    selectAge: 'Selecciona el grupo de edad',
    instructionLanguage: 'Idioma de instrucción',
    secondLanguage: 'Segundo idioma de instrucción',
    none: 'Ninguno',
    loadingLanguages: 'Cargando idiomas…',
    learningRequired: 'Selecciona un grupo de edad y un idioma de instrucción.',
    noInstructionLanguages: 'No hay idiomas de instrucción configurados para este Centro de Aprendizaje de la aplicación móvil.',
    submit: 'Vamos a aprender',
    accountFailed: 'No se pudo crear la cuenta.',
    ages: {
      age_4_8: 'Edades 4–8',
      age_9_13: 'Edades 9–13',
      age_14_18: 'Edades 14–18',
      adult: 'Adultos',
    },
  },
};

type RegistrationCopy = typeof UI_FALLBACK_COPY.en;

const normalizeLocaleCode = (value: string) => String(value || 'en').trim().toLowerCase().replace(/_/g, '-');

const plainStringValue = (value: any) => String(value || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&quot;/gi, '"')
  .replace(/&#39;/gi, "'")
  .replace(/\s+/g, ' ')
  .trim();

const stringRecords = (strings: any) => (
  Array.isArray(strings) ? strings : Object.values(strings || {})
);

const localeCandidates = (localeCode: string) => {
  const normalized = normalizeLocaleCode(localeCode);
  const base = normalized.split('-')[0];
  const norwegianAliases = ['no', 'nb', 'nn'].includes(base) ? ['no', 'nb', 'nn'] : [];

  return Array.from(new Set([
    localeCode,
    normalized,
    normalized.replace(/-/g, '_'),
    base,
    ...norwegianAliases,
  ].filter(Boolean)));
};

const resolveLocaleData = (data: any, candidates: string[]) => {
  if (!data || typeof data !== 'object') return null;

  const keys = Object.keys(data);
  for (const candidate of candidates) {
    const normalizedCandidate = normalizeLocaleCode(candidate);
    const matchedKey = keys.find((key) => normalizeLocaleCode(key) === normalizedCandidate);
    if (matchedKey) return data[matchedKey];
  }

  return null;
};

const resolveLocaleText = (data: any, candidates: string[]) => {
  for (const candidate of candidates) {
    const localized = resolveLocaleData(data, [candidate]);
    const value = plainStringValue(localized?.prettyValue || localized?.stringValue);
    if (value) return value;
  }

  return '';
};

const resolveStringCollectionValue = (
  strings: any,
  localeCode: string,
  canonicalEnglishText: string,
  fallbackText: string,
  stringNumber?: number,
) => {
  const records: any[] = stringRecords(strings);
  const canonical = plainStringValue(canonicalEnglishText).toLowerCase();
  const record = stringNumber
    ? records.find((item) => Number(item?.stringNumber) === Number(stringNumber))
    : records.find((item) => {
      const english = item?.data?.en || item?.data?.['en-US'] || item?.data?.['en_US'];
      return [english?.stringValue, english?.prettyValue]
        .some((value) => plainStringValue(value).toLowerCase() === canonical);
    });

  if (!record?.data) return fallbackText;

  const localizedValue = resolveLocaleText(record.data, localeCandidates(localeCode));
  if (localizedValue) return localizedValue;

  const englishValue = resolveLocaleText(record.data, localeCandidates('en'));
  if (englishValue) return englishValue;

  return fallbackText;
};

const localizeCopyTree = (
  strings: any,
  localeCode: string,
  canonicalValue: any,
  fallbackValue: any,
  path: string[] = [],
): any => {
  if (typeof canonicalValue === 'string') {
    const stringNumber = STRING_NUMBER_BY_COPY_PATH[path.join('.')];
    return resolveStringCollectionValue(strings, localeCode, canonicalValue, fallbackValue, stringNumber);
  }

  if (typeof canonicalValue === 'function') {
    return (...args: any[]) => resolveStringCollectionValue(
      strings,
      localeCode,
      canonicalValue(...args),
      fallbackValue(...args),
    );
  }

  if (canonicalValue && typeof canonicalValue === 'object') {
    return Object.keys(canonicalValue).reduce((result: any, key) => {
      result[key] = localizeCopyTree(
        strings,
        localeCode,
        canonicalValue[key],
        fallbackValue?.[key] ?? canonicalValue[key],
        [...path, key],
      );
      return result;
    }, {});
  }

  return fallbackValue;
};

const buildRegistrationCopy = (strings: any, localeCode: string): RegistrationCopy => {
  const languageCode = normalizeLocaleCode(localeCode).split('-')[0];
  const fallback = languageCode === 'es' ? UI_FALLBACK_COPY.es : UI_FALLBACK_COPY.en;
  const localized = localizeCopyTree(strings, localeCode, UI_FALLBACK_COPY.en, fallback) as RegistrationCopy;
  const continueProvider = resolveStringCollectionValue(strings, localeCode, 'Continue with', 'Continue with', 3791);
  localized.continueApple = `${continueProvider} Apple ID`;
  localized.continueGoogle = `${continueProvider} Google ID`;
  localized.requestNewCodeIn = (seconds: number) => {
    const template = resolveStringCollectionValue(
      strings,
      localeCode,
      'You can request a new verification code in {{seconds}} seconds.',
      languageCode === 'es'
        ? 'Puedes solicitar un nuevo código de verificación en {{seconds}} segundos.'
        : 'You can request a new verification code in {{seconds}} seconds.',
      3845,
    );
    return String(template).replace(/\{\{\s*seconds\s*\}\}/gi, String(seconds));
  };
  return localized;
};

const ageGroupValues = ['age_4_8', 'age_9_13', 'age_14_18', 'adult'] as const;

type IconProps = { className?: string };

const EmailIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
    <path d="m4.75 7 6.56 5.2a1.1 1.1 0 0 0 1.38 0L19.25 7" />
  </svg>
);

const GlobeIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3.5 12h17M12 3c2.45 2.55 3.6 5.55 3.6 9s-1.15 6.45-3.6 9c-2.45-2.55-3.6-5.55-3.6-9S9.55 5.55 12 3Z" />
  </svg>
);

const UserIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
  </svg>
);

const AppleIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.65 12.73c.02 3.1 2.72 4.13 2.75 4.14-.02.08-.43 1.48-1.42 2.93-.86 1.25-1.75 2.5-3.16 2.52-1.38.03-1.83-.82-3.41-.82-1.58 0-2.08.79-3.39.85-1.36.05-2.4-1.36-3.26-2.61-1.77-2.56-3.12-7.23-1.31-10.38.9-1.57 2.5-2.56 4.25-2.59 1.33-.03 2.58.9 3.41.9.83 0 2.39-1.11 4.03-.95.69.03 2.62.28 3.86 2.09-.1.06-2.3 1.34-2.35 3.92ZM14.07 4.98c.72-.87 1.21-2.09 1.08-3.3-1.04.04-2.3.69-3.05 1.56-.67.78-1.26 2.02-1.1 3.21 1.16.09 2.35-.59 3.07-1.47Z" />
  </svg>
);

const RotateDeviceIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="18" y="8" width="28" height="48" rx="5" stroke="currentColor" strokeWidth="3" />
    <path d="M9 30C10.6 19.6 16.7 12.2 25 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M9 30L7 23M9 30L16 27" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M55 34C53.4 44.4 47.3 51.8 39 55" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M55 34L57 41M55 34L48 37" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


const PortraitGuard = ({ copy }: { copy: RegistrationCopy }) => (
  <div className={classes.portraitGuard} role="status" aria-live="polite">
    <RotateDeviceIcon className={classes.rotateIcon} />
    <h1>{copy.rotateTitle}</h1>
    <p>{copy.rotateText}</p>
  </div>
);

function getDeviceId() {
  const saved = localStorage.getItem(DEVICE_KEY);
  if (saved) return saved;
  const generated = typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `mw_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(DEVICE_KEY, generated);
  return generated;
}

function getInitialScreen(): Screen {
  return 'splash';
}

function startPlaySession(result: any, dispatch: any, history: any) {
  if (result.familyAccountToken) localStorage.setItem(FAMILY_ACCOUNT_TOKEN_KEY, result.familyAccountToken);
  dispatch(setAuthFromToken(result.token));
  history.replace(result.redirectTo || '/play');
}

const MobileWrapperRegistration = ({ history }: Props) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  const currentLanguage = useSelector((state: any) => state.status.lang);
  const strings = useSelector((state: any) => state.common?.strings || []);
  const allLocales = useSelector((state: any) => state.common?.locales || []);
  const configuredMobileRegistrationLanguageCodes = useSelector((state: any) => (
    Array.isArray(state.common?.config?.customContentSettings?.mobileWrapper?.registrationLocales)
      ? state.common.config.customContentSettings.mobileWrapper.registrationLocales
      : []
  ));
  const configuredRegistrationLanguageCodes = useSelector((state: any) => (
    Array.isArray(state.common?.config?.registrationLocales)
      ? state.common.config.registrationLocales
      : []
  ));
  const configuredLanguages = useMemo<Option[]>(() => {
    const localeByCode = new Map(
      (Array.isArray(allLocales) ? allLocales : []).map((locale: any) => [
        normalizeLocaleCode(locale?.code),
        locale,
      ])
    );
    const seen = new Set<string>();

    return configuredRegistrationLanguageCodes
      .map((localeCode: any) => String(localeCode || '').trim())
      .filter(Boolean)
      .map((localeCode: string) => {
        const normalizedCode = normalizeLocaleCode(localeCode);
        if (seen.has(normalizedCode)) return null;
        seen.add(normalizedCode);

        const locale: any = localeByCode.get(normalizedCode);
        return {
          value: locale?.code || localeCode,
          label: locale?.displayName || locale?.name || localeCode,
        };
      })
      .filter(Boolean) as Option[];
  }, [allLocales, configuredRegistrationLanguageCodes]);
  const [mobileRegistrationLanguageCodes, setMobileRegistrationLanguageCodes] = useState<string[]>([]);
  const [mobileInstructionLanguageCodes, setMobileInstructionLanguageCodes] = useState<string[]>([]);
  const instructionLocaleOptions = useMemo<Option[]>(
    () => {
      const configured = Array.isArray(configuredLanguages) ? configuredLanguages : [];
      const localeByCode = new Map(
        (Array.isArray(allLocales) ? allLocales : []).map((locale: any) => [
          normalizeLocaleCode(locale?.code),
          locale,
        ])
      );
      configured.forEach((option) => {
        const normalizedCode = normalizeLocaleCode(option.value);
        if (!localeByCode.has(normalizedCode)) {
          localeByCode.set(normalizedCode, {
            code: option.value,
            displayName: option.label,
            name: option.label,
          });
        }
      });

      // Per the mobile spec, Instruction Language is defined by the Solutions
      // inside the single configured Mobile App Product. It is intentionally
      // independent from the languages allowed on the first Registration screen.
      const sourceCodes = mobileInstructionLanguageCodes.length
        ? mobileInstructionLanguageCodes
        : mobileRegistrationLanguageCodes.length
          ? mobileRegistrationLanguageCodes
          : configuredMobileRegistrationLanguageCodes.length
            ? configuredMobileRegistrationLanguageCodes
            : configured.map((option) => option.value);

      const uniqueCodes = Array.from(new Set(
        sourceCodes.map((code: any) => normalizeLocaleCode(code)).filter(Boolean)
      ));

      return uniqueCodes.map((code) => {
        const locale: any = localeByCode.get(code);
        return {
          value: locale?.code || code,
          label: locale?.displayName || locale?.name || code,
        };
      });
    },
    [
      allLocales,
      configuredLanguages,
      configuredMobileRegistrationLanguageCodes,
      mobileInstructionLanguageCodes,
      mobileRegistrationLanguageCodes,
    ],
  );
  const uiLocaleOptions = useMemo<Option[]>(() => {
    const localeByCode = new Map(
      (Array.isArray(allLocales) ? allLocales : []).map((locale: any) => [
        normalizeLocaleCode(locale?.code),
        locale,
      ])
    );

    // This dropdown is the first mobile Registration screen language selector.
    // When mobile-specific Registration languages are configured, they are the
    // source of truth here; do not merge the broader desktop registration list.
    const mobileRegistrationSource = mobileRegistrationLanguageCodes.length
      ? mobileRegistrationLanguageCodes
      : configuredMobileRegistrationLanguageCodes;
    const source = mobileRegistrationSource.length
      ? mobileRegistrationSource
        .map((code: any) => normalizeLocaleCode(code))
        .filter(Boolean)
        .map((code: string) => {
          const locale: any = localeByCode.get(code);
          return {
            value: locale?.code || code,
            label: locale?.displayName || locale?.name || code,
          };
        })
      : (instructionLocaleOptions.length
        ? instructionLocaleOptions
        : (Array.isArray(allLocales) ? allLocales : []).map((locale: any) => ({
          value: String(locale?.code || '').trim(),
          label: String(locale?.displayName || locale?.name || locale?.code || '').trim(),
        })));

    const seen = new Set<string>();
    return source.filter((locale: Option) => {
      const normalized = normalizeLocaleCode(locale.value);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, [allLocales, configuredMobileRegistrationLanguageCodes, instructionLocaleOptions, mobileRegistrationLanguageCodes]);
  const deviceId = useMemo(() => getDeviceId(), []);
  const isNativeMobileApp = useMemo(() => isMobileAppRuntime(), []);
  const mobileAppPlatform = useMemo(() => getMobileAppPlatform(), []);
  const showAppleSignIn = mobileAppPlatform !== 'android';
  const [screen, setScreen] = useState<Screen>(getInitialScreen);
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [providerRegistrationToken, setProviderRegistrationToken] = useState('');
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const accessCodeRef = useRef<HTMLInputElement | null>(null);
  const basicScrollBeforeKeyboardRef = useRef(0);
  const accessCodeRevealTimerRef = useRef<number | null>(null);
  const [registrationCountries, setRegistrationCountries] = useState<Option[]>([
    { value: DEFAULT_COUNTRY, label: DEFAULT_COUNTRY },
  ]);
  const [registrationCountriesLoaded, setRegistrationCountriesLoaded] = useState(false);
  const [values, setValues] = useState<LearningValues>({
    name: '',
    country: DEFAULT_COUNTRY,
    ageBand: 'age_4_8',
    instructionLanguage: '',
    accessCode: '',
  });
  const ageBandAllowsLanguageChoice = values.ageBand === 'age_4_8';
  const allowedInstructionLocaleOptions = useMemo<Option[]>(() => (
    ageBandAllowsLanguageChoice
      ? instructionLocaleOptions
      : instructionLocaleOptions.filter(
        (option) => normalizeLocaleCode(option.value).split('-')[0] === 'en'
      )
  ), [ageBandAllowsLanguageChoice, instructionLocaleOptions]);
  const hasInstructionLanguages = allowedInstructionLocaleOptions.length > 0;
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resendAvailableInSeconds, setResendAvailableInSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [otpDelivery, setOtpDelivery] = useState<OtpDeliveryState>({
    active: false,
    progress: 0,
    message: '',
    detail: '',
  });
  const selectedUiLanguage = currentLanguage?.code || uiLocaleOptions[0]?.value || 'en';
  const copy = useMemo(
    () => buildRegistrationCopy(strings, selectedUiLanguage),
    [strings, selectedUiLanguage],
  );
  const continueWithEmailLabel = copy.continueEmail;

  const revealAccessCode = useCallback(() => {
    const page = pageRef.current;
    const input = accessCodeRef.current;
    if (!page || !input || document.activeElement !== input) return;

    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportHeight = viewport?.height || window.innerHeight || document.documentElement.clientHeight;
    const visibleBottom = viewportTop + viewportHeight - 16;
    const rect = input.getBoundingClientRect();

    // Move only as much as needed to keep the whole field above the keyboard.
    // Do not center it: centering caused the large jump seen on iOS.
    const overlap = rect.bottom - visibleBottom;
    if (overlap <= 0) return;

    page.scrollTo({
      top: Math.max(0, page.scrollTop + overlap + 12),
      behavior: 'smooth',
    });
  }, []);

  const scheduleAccessCodeReveal = useCallback((delay = 120) => {
    if (accessCodeRevealTimerRef.current !== null) {
      window.clearTimeout(accessCodeRevealTimerRef.current);
    }
    accessCodeRevealTimerRef.current = window.setTimeout(() => {
      accessCodeRevealTimerRef.current = null;
      revealAccessCode();
    }, delay);
  }, [revealAccessCode]);

  const handleAccessCodeFocus = useCallback(() => {
    const page = pageRef.current;
    if (page) basicScrollBeforeKeyboardRef.current = page.scrollTop;

    // Wait until iOS finishes changing the visible viewport, then do one smooth
    // minimal scroll. Repeated timed scrolls were the source of the jitter.
    scheduleAccessCodeReveal(180);
  }, [scheduleAccessCodeReveal]);

  const handleAccessCodeBlur = useCallback(() => {
    if (accessCodeRevealTimerRef.current !== null) {
      window.clearTimeout(accessCodeRevealTimerRef.current);
      accessCodeRevealTimerRef.current = null;
    }

    const page = pageRef.current;
    if (!page) return;
    const previousScrollTop = basicScrollBeforeKeyboardRef.current;
    window.setTimeout(() => {
      page.scrollTo({ top: previousScrollTop, behavior: 'smooth' });
    }, 220);
  }, []);

  useEffect(() => {
    if (screen !== 'basic') return undefined;

    // Do not carry focus/keyboard from OTP/Auth into Basic Info.
    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur?.();
    pageRef.current?.scrollTo({ top: 0, behavior: 'auto' });

    const handleViewportResize = () => {
      if (document.activeElement === accessCodeRef.current) {
        // Debounce the iOS keyboard animation and scroll once after it settles.
        scheduleAccessCodeReveal(90);
      }
    };

    window.visualViewport?.addEventListener('resize', handleViewportResize);
    window.addEventListener('resize', handleViewportResize);

    return () => {
      if (accessCodeRevealTimerRef.current !== null) {
        window.clearTimeout(accessCodeRevealTimerRef.current);
        accessCodeRevealTimerRef.current = null;
      }
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('resize', handleViewportResize);
    };
  }, [screen, scheduleAccessCodeReveal]);

  useEffect(() => {
    let active = true;

    familyAgeLanguageAPI.getRegistrationOptions()
      .then((result: any) => {
        if (!active) return;
        if (Array.isArray(result?.registrationLanguages)) {
          setMobileRegistrationLanguageCodes(
            result.registrationLanguages.map((code: any) => String(code || '').trim()).filter(Boolean)
          );
        }
        if (Array.isArray(result?.instructionLanguages)) {
          setMobileInstructionLanguageCodes(
            result.instructionLanguages.map((code: any) => String(code || '').trim()).filter(Boolean)
          );
        }
        if (!result?.success || !Array.isArray(result.countries) || !result.countries.length) return;

        setRegistrationCountries(result.countries);
        const defaultCountry = result.countries.some((item: Option) => item.value === result.defaultCountry)
          ? result.defaultCountry
          : result.countries[0].value;
        setValues((current) => ({ ...current, country: defaultCountry }));
        setRegistrationCountriesLoaded(true);
      })
      .catch(() => undefined);

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!registrationCountriesLoaded || !values.country) return undefined;
    let active = true;

    familyAgeLanguageAPI.getRegistrationOptions({ country: values.country })
      .then((result: any) => {
        if (!active || !result?.success) return;
        if (Array.isArray(result.registrationLanguages)) {
          setMobileRegistrationLanguageCodes(
            result.registrationLanguages.map((code: any) => String(code || '').trim()).filter(Boolean)
          );
        }
        if (Array.isArray(result.instructionLanguages)) {
          setMobileInstructionLanguageCodes(
            result.instructionLanguages.map((code: any) => String(code || '').trim()).filter(Boolean)
          );
        }
      })
      .catch(() => undefined);

    return () => { active = false; };
  }, [registrationCountriesLoaded, values.country]);

  useEffect(() => {
    const timer = screen === 'splash' ? window.setTimeout(() => setScreen('auth'), 1100) : null;
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [screen]);

  useEffect(() => {
    if (auth?.isAuthenticated) history.replace('/play');
  }, [auth?.isAuthenticated, history]);

  useEffect(() => {
    if (!uiLocaleOptions.length) return;
    const currentIsAllowed = uiLocaleOptions.some(
      (option) => normalizeLocaleCode(option.value) === normalizeLocaleCode(currentLanguage?.code)
    );
    if (!currentIsAllowed) dispatch(changeLocale(uiLocaleOptions[0].value));
  }, [currentLanguage?.code, dispatch, uiLocaleOptions]);

  const applyOtpFromPush = useCallback((payload: any) => {
    const code = String(payload?.otp || '').replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) return;

    setOtp(code.split(''));
    if (payload?.challengeId) setChallengeId(String(payload.challengeId));
    setScreen('verify');
    setNotice(copy.pushCodeReceived);
    setError('');
    window.setTimeout(() => otpRefs.current[5]?.focus(), 0);
  }, [copy.pushCodeReceived]);

  useEffect(() => {
    if (!isNativeMobileApp) return undefined;

    initializeMobilePushBridge();
    const pending = consumePendingOtpNotification();
    if (pending) applyOtpFromPush(pending);

    const handleOtpNotification = (event: any) => applyOtpFromPush(event?.detail || {});
    window.addEventListener(MOBILE_OTP_EVENT, handleOtpNotification);
    return () => window.removeEventListener(MOBILE_OTP_EVENT, handleOtpNotification);
  }, [applyOtpFromPush, isNativeMobileApp]);

  useEffect(() => {
    if (!allowedInstructionLocaleOptions.length) {
      setValues((current) => (
        current.instructionLanguage ? { ...current, instructionLanguage: '' } : current
      ));
      return;
    }

    setValues((current) => {
      const currentInstructionLanguage = String(current.instructionLanguage || '').trim();
      const currentIsAllowed = Boolean(currentInstructionLanguage) && allowedInstructionLocaleOptions.some(
        (option) => normalizeLocaleCode(option.value) === normalizeLocaleCode(current.instructionLanguage)
      );
      const nextInstructionLanguage = currentIsAllowed
        ? current.instructionLanguage
        : allowedInstructionLocaleOptions[0].value;

      return nextInstructionLanguage === current.instructionLanguage
        ? current
        : { ...current, instructionLanguage: nextInstructionLanguage };
    });
  }, [allowedInstructionLocaleOptions]);

  useEffect(() => {
    if (screen !== 'verify' || resendAvailableInSeconds <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setResendAvailableInSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [screen, resendAvailableInSeconds]);

  const update = (key: keyof LearningValues, value: string) => {
    setValues((current) => {
      if (
        key === 'instructionLanguage'
        && normalizeLocaleCode(value).split('-')[0] !== 'en'
        && current.ageBand !== 'age_4_8'
      ) {
        return { ...current, instructionLanguage: value, ageBand: 'age_4_8' };
      }

      if (key === 'ageBand' && value !== 'age_4_8') {
        const englishOption = instructionLocaleOptions.find(
          (option) => normalizeLocaleCode(option.value).split('-')[0] === 'en'
        );
        return {
          ...current,
          ageBand: value,
          instructionLanguage: englishOption?.value || '',
        };
      }

      return { ...current, [key]: value };
    });
  };

  const sendOtp = async (isResend = false, provider?: 'apple' | 'google') => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setNotice('');
      const providerLabel = provider === 'apple' ? 'Apple ID' : provider === 'google' ? 'Google ID' : '';
      setError(providerLabel ? copy.invalidProviderEmail(providerLabel) : copy.invalidEmail);
      return;
    }

    setBusy(true);
    setError('');
    setNotice('');
    setProviderRegistrationToken('');

    if (isNativeMobileApp) {
      setOtpDelivery({
        active: true,
        progress: 12,
        message: copy.preparingVerification,
        detail: copy.checkingNotifications,
      });
    }

    try {
      const pushContext = isNativeMobileApp
        ? await getMobilePushContext({ waitForRegistrationMs: 3000 })
        : { available: false, permission: 'web', token: '', platform: 'web' };

      if (isNativeMobileApp) {
        setOtpDelivery({
          active: true,
          progress: 38,
          message: copy.connectingDevice,
          detail: pushContext.permission === 'granted'
            ? copy.notificationsAllowed
            : copy.emailStillAvailable,
        });
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        setOtpDelivery({
          active: true,
          progress: 66,
          message: copy.sendingVerification,
          detail: copy.requestingCode,
        });
      }

      const result = await api.mobileWrapper.sendOtp({
        email: email.trim(),
        deviceId,
        ...(isResend ? { resend: true, ...(challengeId ? { challengeId } : {}) } : {}),
      });

      if (!result?.success || !result?.challengeId) {
        setError(result?.error || copy.unableSend);
        if (isNativeMobileApp) {
          setOtpDelivery({
            active: false,
            progress: 0,
            message: '',
            detail: '',
          });
        }
        return;
      }

      if (isNativeMobileApp) {
        setOtpDelivery({
          active: true,
          progress: 88,
          message: copy.waitingDelivery,
          detail: copy.checkEmail,
        });
      }

      setChallengeId(result.challengeId);
      setOtp(['', '', '', '', '', '']);
      setResendAvailableInSeconds(Number(result.resendAvailableInSeconds) || 0);
      setScreen('verify');

      const providerLabel = provider === 'apple' ? 'Apple ID' : provider === 'google' ? 'Google ID' : '';
      const deliveryNotice = providerLabel ? copy.providerOtpNotice(providerLabel) : copy.codeRequested;

      setNotice(isResend ? copy.newCodeRequested : deliveryNotice);

      if (isNativeMobileApp) {
        setOtpDelivery({
          active: false,
          progress: 100,
          message: copy.verificationRequested,
          detail: deliveryNotice,
        });
      }
    } catch (requestError: any) {
      const response = requestError?.response?.data;
      const retryAfterSeconds = Number(response?.retryAfterSeconds) || 0;
      if (retryAfterSeconds > 0) setResendAvailableInSeconds(retryAfterSeconds);
      setError(
        retryAfterSeconds > 0
          ? copy.requestNewCodeIn(retryAfterSeconds)
          : (response?.error || requestError?.message || copy.unableSend)
      );
      if (isNativeMobileApp) {
        setOtpDelivery({
          active: false,
          progress: 0,
          message: '',
          detail: '',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const updateOtp = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '');
    if (!digits) {
      setOtp((current) => current.map((value, position) => (position === index ? '' : value)));
      return;
    }
    const next = [...otp];
    digits.slice(0, 6 - index).split('').forEach((digit, offset) => { next[index + offset] = digit; });
    setOtp(next);
    const target = Math.min(index + digits.length, 5);
    otpRefs.current[target]?.focus();
  };

  const handleOtpKeyDown = (index: number, event: any) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (event: any) => {
    const code = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) return;
    event.preventDefault();
    setOtp(code.split(''));
    otpRefs.current[5]?.focus();
  };

  const verifyOtp = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      setError(copy.enterCode);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await api.mobileWrapper.verifyOtp({ email: email.trim(), otp: code, challengeId, deviceId });
      if (result?.token) {
        startPlaySession(result, dispatch, history);
        return;
      }
      if (result?.mobileFamilyState === 'suspended') {
        history.replace('/?mobileFamily=suspended');
        return;
      }
      /*
       * User / Family or Friend picker is parked for now. The backend now
       * returns a learner token for existing mobile family accounts after OTP.
       * Keep this branch documented for the later co-play/member-picker return.
       *
       * Previous flow:
       * if (result?.next === 'family_login' && result?.familyAccountToken) {
       *   localStorage.setItem(FAMILY_ACCOUNT_TOKEN_KEY, result.familyAccountToken);
       *   history.replace('/');
       *   return;
       * }
       */
      if (result?.next === 'complete_registration' && result?.verificationToken) {
        setVerificationToken(result.verificationToken);
        setScreen('basic');
        return;
      }
      setError(copy.verifyFailed);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || requestError?.message || copy.verifyFailed);
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = () => sendOtp(true);

  const clearOtp = () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    setNotice('');
    window.setTimeout(() => otpRefs.current[0]?.focus(), 0);
  };

  const nextBasicInfo = () => {
    if (values.name.trim().length < 3 || !values.country) {
      setError(copy.basicRequired);
      return;
    }
    setError('');
    setScreen('learning');
  };

  const submitRegistration = async () => {
    if (!hasInstructionLanguages) {
      setError(copy.noInstructionLanguages);
      return;
    }
    if (!values.ageBand || !values.instructionLanguage) {
      setError(copy.learningRequired);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const registrationPayload = {
        ...values,
        secondInstructionLanguage: null,
        name: values.name.trim(),
        email: email.trim(),
        challengeId,
        verificationToken,
        providerRegistrationToken,
        deviceId,
        deviceInfo: { userAgent: window.navigator.userAgent },
      };
      const result = await api.mobileWrapper.register(registrationPayload);
      if (!result?.token) {
        setError(result?.error || result?.message || copy.accountFailed);
        return;
      }
      startPlaySession(result, dispatch, history);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || requestError?.message || copy.accountFailed);
    } finally {
      setBusy(false);
    }
  };


  const requestAppleSignIn = async () => {
    setBusy(true);
    setError('');
    setNotice(copy.appleSigningIn);
    setVerificationToken('');
    setChallengeId(null);

    try {
      const credential = await signInWithAppleNative();
      const result = await api.mobileWrapper.signInWithApple({
        identityToken: credential?.identityToken,
        nonce: credential?.nonce,
        authorizationCode: credential?.authorizationCode,
        givenName: credential?.givenName,
        familyName: credential?.familyName,
        deviceId,
        deviceInfo: { userAgent: window.navigator.userAgent },
        platform: 'ios',
      });

      if (result?.token) {
        startPlaySession(result, dispatch, history);
        return;
      }
      if (result?.mobileFamilyState === 'suspended') {
        history.replace('/?mobileFamily=suspended');
        return;
      }

      if (result?.next === 'complete_registration' && result?.providerRegistrationToken) {
        const suggestedName = [result.givenName, result.familyName].filter(Boolean).join(' ').trim();
        setProviderRegistrationToken(result.providerRegistrationToken);
        setVerificationToken('');
        setChallengeId(null);
        setEmail(result.email || '');
        if (suggestedName) {
          setValues((current) => ({ ...current, name: current.name || suggestedName }));
        }
        setNotice('');
        setScreen('basic');
        return;
      }

      setError(result?.error || copy.appleSignInFailed);
      setNotice('');
    } catch (requestError: any) {
      const code = requestError?.code || requestError?.response?.data?.code;
      const cancelled = code === 'APPLE_SIGN_IN_CANCELLED' || code === '1001';
      setError(cancelled
        ? copy.appleSignInCancelled
        : requestError?.response?.data?.error || requestError?.message || copy.appleSignInFailed);
      setNotice('');
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <header className={classes.header}>
      <img className={classes.logo} src={mobileLogo} alt="Family Learning Company" />
      <label className={classes.uiLocale}>
        <GlobeIcon className={classes.localeIcon} />
        <select value={selectedUiLanguage} onChange={(event) => dispatch(changeLocale(event.target.value))} aria-label={copy.pageLanguage}>
          {uiLocaleOptions.map((locale) => <option key={locale.value} value={locale.value}>{locale.label}</option>)}
        </select>
      </label>
    </header>
  );

  if (screen === 'splash') {
    return (
      <div className={classes.splash}>
        <PortraitGuard copy={copy} />
        <img src={mobileLogo} alt="Family Learning Company" />
      </div>
    );
  }

  return (
    <div ref={pageRef} className={classes.page}>
      <PortraitGuard copy={copy} />
      {header}

      {screen === 'auth' && (
        <main className={classes.authPanel}>
          <label className={classes.fieldLabel} htmlFor="mobile-signup-email">{copy.email}</label>
          <div className={classes.iconInput}>
            <EmailIcon className={classes.inputIcon} />
            <input
              id="mobile-signup-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
            />
          </div>
          <button type="button" className={classes.primaryWide} onClick={() => sendOtp()} disabled={busy}>
            {busy ? copy.sendingCode : continueWithEmailLabel} <span>→</span>
          </button>
          {isNativeMobileApp && otpDelivery.active && (
            <div className={classes.otpDeliveryStatus} role="status" aria-live="polite">
              <div className={classes.otpDeliveryHeadline}>
                <strong>{otpDelivery.message}</strong>
                <span>{otpDelivery.progress}%</span>
              </div>
              <div className={classes.otpDeliveryTrack}><i style={{ width: `${otpDelivery.progress}%` }} /></div>
              <small>{otpDelivery.detail}</small>
            </div>
          )}
          {showAppleSignIn && <div className={classes.divider}><span>{copy.continueWith}</span></div>}
          {showAppleSignIn && (
            <button type="button" className={classes.providerButton} onClick={requestAppleSignIn} disabled={busy}><AppleIcon className={`${classes.providerIcon} ${classes.appleIcon}`} /> {copy.continueApple} <span className={classes.actionArrow}>›</span></button>
          )}
          {error && <p className={classes.error}>{error}</p>}
        </main>
      )}

      {screen === 'verify' && (
        <main className={classes.verifyPanel}>
          <h1>{copy.verifyTitle}</h1>
          <p className={classes.subtitle}>{copy.verifySubtitle}</p>
          <div className={classes.otpFields}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(node) => { otpRefs.current[index] = node; }}
                value={digit}
                onChange={(event) => updateOtp(index, event.target.value)}
                onKeyDown={(event) => handleOtpKeyDown(index, event)}
                onPaste={handleOtpPaste}
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={6}
                aria-label={`${copy.digit} ${index + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            className={classes.clearOtpButton}
            onClick={clearOtp}
            disabled={busy || !otp.some(Boolean)}
          >
            {copy.clear}
          </button>
          <button type="button" className={classes.resendButton} onClick={resendOtp} disabled={busy || resendAvailableInSeconds > 0}>
            {resendAvailableInSeconds > 0 ? copy.resendIn(resendAvailableInSeconds) : copy.resend}
          </button>
          {isNativeMobileApp && otpDelivery.active && otpDelivery.progress > 0 && (
            <div className={classes.otpDeliveryInline}>
              <span>…</span>
              <small>{otpDelivery.detail || otpDelivery.message}</small>
            </div>
          )}
          {notice && <p className={classes.notice}>{copy.codeRequested}</p>}
          {error && <p className={classes.error}>{error}</p>}
          <div className={classes.verifyActions}>
            <button type="button" className={classes.secondaryButton} onClick={() => setScreen('auth')}>←&nbsp; {copy.back}</button>
            <button type="button" className={classes.primaryButton} onClick={verifyOtp} disabled={busy}>{copy.verify} &nbsp;→</button>
          </div>
        </main>
      )}

      {screen === 'basic' && (
        <main className={`${classes.setupPanel} ${classes.basicPanel}`}>
          <h1>{copy.basicTitle}</h1>
          <p className={classes.subtitle}>{copy.basicSubtitle}</p>
          <label className={`${classes.fieldLabel} ${classes.emphasis}`} htmlFor="mobile-name">{copy.name}</label>
          <div className={classes.iconInput}>
            <UserIcon className={classes.inputIcon} />
            <input id="mobile-name" value={values.name} onChange={(event) => update('name', event.target.value)} placeholder={copy.namePlaceholder} autoComplete="off" />
          </div>
          <label className={`${classes.fieldLabel} ${classes.emphasis}`} htmlFor="mobile-country">{copy.country}</label>
          <div className={classes.countrySelect}>
            <span aria-hidden="true">🌐</span>
            <select id="mobile-country" value={values.country} onChange={(event) => update('country', event.target.value)}>
              {registrationCountries.map((country) => (
                <option key={country.value} value={country.value}>{country.label}</option>
              ))}
            </select>
          </div>
          <label className={`${classes.fieldLabel} ${classes.emphasis}`} htmlFor="mobile-access-code">{copy.accessCode}</label>
          <div className={classes.iconInput}>
            <input
              ref={accessCodeRef}
              id="mobile-access-code"
              value={values.accessCode}
              onChange={(event) => update('accessCode', event.target.value)}
              onFocus={handleAccessCodeFocus}
              onBlur={handleAccessCodeBlur}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              placeholder={copy.accessCodePlaceholder}
              autoComplete="off"
              enterKeyHint="done"
            />
          </div>
          {error && <p className={classes.error}>{error}</p>}
          <div className={classes.setupActions}>
            <button type="button" className={classes.secondaryButton} onClick={() => setScreen('auth')}>←&nbsp; {copy.back}</button>
            <button type="button" className={classes.primaryButton} onClick={nextBasicInfo}>{copy.next} &nbsp;→</button>
          </div>
        </main>
      )}

      {screen === 'learning' && (
        <main className={`${classes.setupPanel} ${classes.learningPanel}`}>
          <h1>{copy.learningTitle}</h1>
          <p className={classes.subtitle}>{copy.learningSubtitle}</p>
          <div className={classes.learningGrid}>
            <fieldset className={classes.ageGroup}>
              <legend>{copy.selectAge}</legend>
              {ageGroupValues.map((ageValue) => (
                <label key={ageValue}>
                  <input
                    type="radio"
                    name="mobile-age"
                    value={ageValue}
                    checked={values.ageBand === ageValue}
                    onChange={(event) => update('ageBand', event.target.value)}
                  />
                  <span>{copy.ages[ageValue]}</span>
                </label>
              ))}
            </fieldset>
            <section className={classes.languages}>
              <label className={`${classes.fieldLabel} ${classes.emphasis}`} htmlFor="mobile-instruction-language">{copy.instructionLanguage}</label>
              <select
                id="mobile-instruction-language"
                value={values.instructionLanguage}
                onChange={(event) => update('instructionLanguage', event.target.value)}
                disabled={!hasInstructionLanguages || !ageBandAllowsLanguageChoice}
              >
                {allowedInstructionLocaleOptions.map((locale) => <option key={locale.value} value={locale.value}>{locale.label}</option>)}
              </select>
            </section>
          </div>
          {!hasInstructionLanguages
            ? <p className={classes.error}>{copy.noInstructionLanguages}</p>
            : error && <p className={classes.error}>{error}</p>}
          <div className={classes.setupActions}>
            <button type="button" className={classes.secondaryButton} onClick={() => setScreen('basic')}>←&nbsp; {copy.back}</button>
            <button type="button" className={classes.primaryButton} onClick={submitRegistration} disabled={busy || !hasInstructionLanguages}>{copy.submit} &nbsp;→</button>
          </div>
        </main>
      )}
    </div>
  );
};

export default MobileWrapperRegistration;
